import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellOff, Headphones, MoreHorizontal, Pin, Search, Send, UserX } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { IS_API_MODE } from "../../shared/config/runtime";
import {
  createChatSocket,
  createDirectConversation,
  CHAT_PROTOCOL_VERSION,
  listConversations,
  listMessages,
  markConversationRead,
  requestRealtimeTicket,
} from "../../features/chat/api";
import {
  appendMessageItem,
  getConversationListItems,
  getEnvelopeConversationId,
  getEnvelopeSenderId,
  getMessageListItems,
  getReadReceiptConversationId,
  getReadReceiptSeq,
  getReadReceiptUserId,
  normalizeConversation,
  normalizeMessage,
} from "../../features/chat/normalize";
import { getApiErrorMessage } from "../../shared/api/errors";
import { useAuthStore } from "../../features/auth/store";
import { showToast } from "../../shared/ui/toast";

function formatMessageTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
}

export default function MessagesPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pendingOutboundRef = useRef(null);
  const chatMenuRef = useRef(null);
  const messageStreamRef = useRef(null);
  const userId = useAuthStore((state) => state.user?.id);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const conversationsQuery = useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: listConversations,
    enabled: IS_API_MODE,
  });
  const apiConversations = useMemo(
    () => {
      const data = getConversationListItems(conversationsQuery.data);
      return data.map(normalizeConversation).sort((a, b) => {
        const aSupport = a.type === "support" || a.support;
        const bSupport = b.type === "support" || b.support;
        return Number(bSupport) - Number(aSupport);
      });
    },
    [conversationsQuery.data],
  );
  const [conversationPreferences, setConversationPreferences] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem(`chat-conversation-preferences:${userId || "anonymous"}`) || "{}");
    } catch {
      return {};
    }
  });
  const availableConversations = useMemo(
    () => [...apiConversations].sort(
      (a, b) => Number(Boolean(conversationPreferences[b.id]?.pinned))
        - Number(Boolean(conversationPreferences[a.id]?.pinned)),
    ),
    [apiConversations, conversationPreferences],
  );
  const [activeId, setActiveId] = useState(availableConversations[0]?.id);
  const active = availableConversations.find((item) => String(item.id) === String(activeId));
  const messagesQuery = useQuery({
    queryKey: ["chat", "messages", activeId],
    queryFn: () => listMessages(activeId),
    enabled: IS_API_MODE && Boolean(activeId),
  });
  const [message, setMessage] = useState("");
  const [socketError, setSocketError] = useState("");
  const [socketStatus, setSocketStatus] = useState("idle");
  const [peerReadByConversation, setPeerReadByConversation] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(`chat-peer-read:${userId || "anonymous"}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const peerReadSeq = Number(peerReadByConversation[activeId] || 0);
  const [directConversationError, setDirectConversationError] = useState("");
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const targetUserId = location.state?.targetUserId;
  const activePreferences = conversationPreferences[activeId] || {};

  useEffect(() => {
    try {
      window.localStorage.setItem(`chat-conversation-preferences:${userId || "anonymous"}`, JSON.stringify(conversationPreferences));
    } catch {
      // 本地存储不可用时仍保留当前页面内的设置。
    }
  }, [conversationPreferences, userId]);

  useEffect(() => {
    if (!chatMenuOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (!chatMenuRef.current?.contains(event.target)) setChatMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [chatMenuOpen]);

  useEffect(() => {
    setChatMenuOpen(false);
  }, [activeId]);

  function toggleConversationPreference(key, enabledMessage, disabledMessage) {
    if (!activeId) return;
    const nextEnabled = !activePreferences[key];
    setConversationPreferences((current) => {
      const conversationKey = String(activeId);
      const currentSettings = current[conversationKey] || {};
      return {
        ...current,
        [conversationKey]: { ...currentSettings, [key]: nextEnabled },
      };
    });
    setChatMenuOpen(false);
    showToast(nextEnabled ? enabledMessage : disabledMessage, "success");
  }

  useEffect(() => {
    try {
      window.sessionStorage.setItem(`chat-peer-read:${userId || "anonymous"}`, JSON.stringify(peerReadByConversation));
    } catch {
      // 存储不可用时不影响实时聊天。
    }
  }, [peerReadByConversation, userId]);

  useEffect(() => {
    if (!IS_API_MODE || !targetUserId || !isAuthenticated) return undefined;
    let disposed = false;
    setDirectConversationError("");
    createDirectConversation(targetUserId)
      .then((result) => {
        if (disposed) return;
        const conversationId = result?.conversation_id || result?.conversationId || result?.id;
        if (conversationId) setActiveId(String(conversationId));
        queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
        navigate("/messages", { replace: true, state: null });
      })
      .catch((error) => {
        if (!disposed) setDirectConversationError(getApiErrorMessage(error, "私信会话创建失败"));
      });
    return () => {
      disposed = true;
    };
  }, [isAuthenticated, navigate, queryClient, targetUserId]);

  useEffect(() => {
    if (!availableConversations.length) return;
    if (!availableConversations.some((item) => String(item.id) === String(activeId))) {
      setActiveId(availableConversations[0].id);
    }
  }, [activeId, availableConversations]);

  useEffect(() => {
    if (!apiConversations.length) return;
    setPeerReadByConversation((current) => {
      let changed = false;
      const next = { ...current };
      apiConversations.forEach((conversation) => {
        const conversationKey = String(conversation.id);
        const serverReadSeq = Number(conversation.peerReadSeq || 0);
        if (serverReadSeq > Number(next[conversationKey] || 0)) {
          next[conversationKey] = serverReadSeq;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [apiConversations]);

  useEffect(() => {
    if (!IS_API_MODE || !active) return undefined;
    let disposed = false;
    let socket;
    let heartbeatTimer;
    let reconnectAttempt = 0;
    const sendReadReceipt = () => {
      const latestMessages = getMessageListItems(queryClient.getQueryData(["chat", "messages", activeId]));
      if (!socket || socket.readyState !== WebSocket.OPEN || !activeId || !latestMessages.length) return;
      const lastReadSeq = latestMessages.reduce(
        (maxSeq, item) => Math.max(maxSeq, Number(item.seq || 0)),
        0,
      );
      if (lastReadSeq > 0) {
        socket.send(JSON.stringify({
          version: CHAT_PROTOCOL_VERSION,
          type: "message.read",
          conversationId: String(activeId),
          payload: { lastReadSeq: String(lastReadSeq) },
        }));
      }
    };
    async function connect() {
      try {
        setSocketStatus("connecting");
        const ticket = await requestRealtimeTicket();
        if (disposed) return;
        socket = createChatSocket(ticket);
        socketRef.current = socket;
        socket.onopen = () => {
          reconnectAttempt = 0;
          setSocketStatus("open");
          setSocketError("");
          heartbeatTimer = window.setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ version: CHAT_PROTOCOL_VERSION, type: "presence.ping", payload: {} }));
            }
          }, 30000);
          if (activeId) {
            socket.send(JSON.stringify({ version: CHAT_PROTOCOL_VERSION, type: "conversation.join", conversationId: String(activeId), payload: {} }));
            if (pendingOutboundRef.current) {
              socket.send(JSON.stringify({
                version: CHAT_PROTOCOL_VERSION,
                type: "message.send",
                conversationId: String(activeId),
                payload: { text: pendingOutboundRef.current, clientMessageId: crypto.randomUUID() },
              }));
              pendingOutboundRef.current = null;
            }
          }
        };
        socket.onmessage = (event) => {
          const incoming = JSON.parse(event.data);
          if (incoming.type === "conversation.joined") {
            sendReadReceipt();
          }
          if (incoming.type === "message.new" || incoming.type === "chat.message") {
            const conversationId = getEnvelopeConversationId(incoming);
            const messagePayload = incoming.payload || incoming.message || incoming;
            queryClient.setQueryData(
              ["chat", "messages", conversationId],
              (items = []) => appendMessageItem(items, messagePayload),
            );
            queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
            if (getEnvelopeSenderId(incoming) !== String(userId)) {
              window.setTimeout(sendReadReceipt, 0);
            }
          }
          if (incoming.type === "chat.read" || incoming.type === "message.read") {
            const payload = incoming.payload || {};
            if (getReadReceiptConversationId(payload) === String(activeId)
              && getReadReceiptUserId(payload) !== String(userId)) {
              const nextReadSeq = getReadReceiptSeq(payload);
              setPeerReadByConversation((current) => {
                const conversationKey = String(activeId);
                const previousReadSeq = Number(current[conversationKey] || 0);
                if (nextReadSeq <= previousReadSeq) return current;
                return { ...current, [conversationKey]: nextReadSeq };
              });
            }
          }
          if (incoming.type === "error") {
            setSocketError(incoming.payload?.detail || "消息服务暂不可用");
          }
        };
        socket.onerror = () => {
          setSocketStatus("closed");
          setSocketError("实时消息连接失败，暂时无法发送消息");
        };
        socket.onclose = (event) => {
          if (heartbeatTimer) window.clearInterval(heartbeatTimer);
          if (disposed) return;
          setSocketStatus("closed");
          const closeHint = event?.code && event.code !== 1000 ? `（连接码 ${event.code}）` : "";
          setSocketError(`实时消息连接已断开${closeHint}，正在重试……`);
          const delay = Math.min(1000 * (2 ** reconnectAttempt), 10000);
          reconnectAttempt += 1;
          reconnectTimerRef.current = window.setTimeout(connect, delay);
        };
      } catch (error) {
        if (disposed) return;
        setSocketStatus("closed");
        setSocketError(`${getApiErrorMessage(error, "实时消息初始化失败")}，正在重试……`);
        const delay = Math.min(1000 * (2 ** reconnectAttempt), 10000);
        reconnectAttempt += 1;
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      }
    }
    connect();
    return () => {
      disposed = true;
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      socket?.close();
      socketRef.current = null;
      setSocketStatus("idle");
    };
  }, [activeId, Boolean(active), queryClient]);

  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN && activeId) {
      socket.send(JSON.stringify({ version: CHAT_PROTOCOL_VERSION, type: "conversation.join", conversationId: String(activeId), payload: {} }));
    }
    const visibleMessages = getMessageListItems(messagesQuery.data);
    if (activeId && visibleMessages.length) {
      const lastReadSeq = visibleMessages.reduce(
        (maxSeq, item) => Math.max(maxSeq, Number(item.seq || 0)),
        0,
      );
      if (lastReadSeq > 0) {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({
            version: CHAT_PROTOCOL_VERSION,
            type: "message.read",
            conversationId: String(activeId),
            payload: { lastReadSeq: String(lastReadSeq) },
          }));
        } else {
          markConversationRead(activeId, lastReadSeq).catch(() => {});
        }
        queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      }
    }
  }, [activeId, messagesQuery.data, socketStatus, userId]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const stream = messageStreamRef.current;
      if (stream) stream.scrollTop = stream.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [activeId, messagesQuery.data]);

  if (!active && IS_API_MODE && conversationsQuery.isPending) {
    return <div className="detail-state">正在加载消息……</div>;
  }
  if (!active && IS_API_MODE && conversationsQuery.isError) {
    return <div className="detail-state error">{getApiErrorMessage(conversationsQuery.error, "消息加载失败")}</div>;
  }
  const remoteMessages = IS_API_MODE
    ? getMessageListItems(messagesQuery.data)
      .map((item) => normalizeMessage(item, userId))
      .sort((a, b) => {
        const seqDifference = Number(a.seq || 0) - Number(b.seq || 0);
        if (seqDifference !== 0) return seqDifference;
        return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      })
    : [];
  const displayedMessages = remoteMessages;
  const lastSentMessageId = displayedMessages.reduce((latest, item) => {
    if (!item.sent) return latest;
    if (!latest || Number(item.seq || 0) >= Number(latest.seq || 0)) return item;
    return latest;
  }, null)?.id;
  return (
    <section className="messages-page">
      <aside className="conversation-sidebar">
        <label className="conversation-search">
          <Search size={18} />
          <input placeholder="搜索消息" />
        </label>
        <div className="conversation-list">
          {availableConversations.length === 0 ? <p className="conversation-empty">暂无会话</p> : null}
          {availableConversations.map((conversation) => (
            <button
              key={conversation.id}
              className={activeId === conversation.id ? "active" : ""}
              onClick={() => setActiveId(conversation.id)}
            >
              <img className="avatar avatar-lg" src={conversation.avatar} alt="" />
              <span>
                <strong>{conversation.name}</strong>
                <small>
                  {conversation.type === "support" || conversation.support ? "专属客服 · " : ""}{conversation.preview}
                </small>
              </span>
              {conversationPreferences[conversation.id]?.muted || conversationPreferences[conversation.id]?.pinned ? (
                <span className="conversation-flags" aria-label="会话设置状态">
                  {conversationPreferences[conversation.id]?.muted ? <BellOff size={13} /> : null}
                  {conversationPreferences[conversation.id]?.pinned ? <Pin size={13} /> : null}
                </span>
              ) : null}
              {conversation.unread ? <b>{conversation.unread}</b> : null}
            </button>
          ))}
        </div>
      </aside>
      <div className="mobile-conversation-picker" aria-label="选择会话">
        {availableConversations.map((conversation) => (
          <button
            key={conversation.id}
            className={String(activeId) === String(conversation.id) ? "active" : ""}
            onClick={() => setActiveId(conversation.id)}
          >
            <img className="avatar avatar-sm" src={conversation.avatar} alt="" />
            <span>{conversation.name}</span>
          </button>
        ))}
      </div>
      <div className="chat-panel">
        {directConversationError ? <div className="message-status error direct-conversation-error">{directConversationError}</div> : null}
        {active ? (
          <>
            <header className="chat-header">
              <img className="avatar avatar-md" src={active.avatar} alt="" />
              <span>
                <strong>{active.name}</strong>
                {active.type === "support" || active.support ? <small>专属客服</small> : null}
              </span>
              {active.type === "support" || active.support ? <span className="chat-header-label">固定客服会话</span> : null}
              <div className="chat-actions" ref={chatMenuRef}>
                <button
                  type="button"
                  className="chat-more-button"
                  aria-label="会话设置"
                  aria-expanded={chatMenuOpen}
                  onClick={() => setChatMenuOpen((open) => !open)}
                >
                  <MoreHorizontal size={22} />
                </button>
                {chatMenuOpen ? (
                  <div className="chat-actions-menu">
                    <button type="button" onClick={() => toggleConversationPreference("muted", "已开启消息免打扰", "已关闭消息免打扰")}>
                      <BellOff size={17} />
                      {activePreferences.muted ? "关闭消息免打扰" : "消息免打扰"}
                    </button>
                    <button type="button" className="danger" onClick={() => toggleConversationPreference("blocked", "已拉黑该用户", "已取消拉黑")}>
                      <UserX size={17} />
                      {activePreferences.blocked ? "取消拉黑" : "拉黑"}
                    </button>
                    <button type="button" onClick={() => toggleConversationPreference("pinned", "已置顶聊天", "已取消置顶")}>
                      <Pin size={17} />
                      {activePreferences.pinned ? "取消置顶聊天" : "置顶聊天"}
                    </button>
                  </div>
                ) : null}
              </div>
            </header>
            <div className="message-stream" ref={messageStreamRef}>
              {messagesQuery.isPending && IS_API_MODE ? (
                <div className="message-status">正在加载历史消息……</div>
              ) : null}
              {displayedMessages.map((item) => (
                <div className={`message-row ${item.sent ? "sent" : "received"}`} key={item.id}>
                  <button
                    className="message-avatar-button"
                    aria-label={`查看${item.senderName}的主页`}
                    onClick={() => navigate(item.senderId === String(userId) ? "/profile" : `/users/${item.senderId}`)}
                  >
                    <img className="avatar avatar-sm" src={item.avatar} alt="" />
                  </button>
                  <div className="message-wrap">
                    <div className={`message ${item.sent ? "sent" : "received"}`}>
                      {item.text}
                    </div>
                    <div className="message-meta">
                      <time>{formatMessageTime(item.createdAt)}</time>
                      {item.sent && item.id === lastSentMessageId ? (
                        <span>{Number(item.seq) > 0 && peerReadSeq >= Number(item.seq) ? "已读" : "已发送"}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
              {socketError ? <div className="message-status error">{socketError}</div> : null}
            </div>
            <form
              className="message-composer"
              onSubmit={(event) => {
                event.preventDefault();
                if (!message.trim()) return;
                if (activePreferences.blocked) {
                  showToast("请先取消拉黑后再发送消息", "error");
                  return;
                }
                const content = message.trim();
                if (IS_API_MODE && socketRef.current?.readyState === WebSocket.OPEN) {
                  socketRef.current.send(JSON.stringify({
                    version: CHAT_PROTOCOL_VERSION,
                    type: "message.send",
                    conversationId: String(activeId),
                    payload: { text: content, clientMessageId: crypto.randomUUID() },
                  }));
                } else {
                  pendingOutboundRef.current = content;
                  setSocketError(socketStatus === "connecting" ? "消息连接正在建立，消息将在连接后发送" : "消息连接尚未建立，消息将在重连后发送");
                }
                setMessage("");
              }}
            >
              <input
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={activePreferences.blocked ? "已拉黑该用户" : "发消息……"}
                disabled={activePreferences.blocked}
              />
              <button className="icon-button" disabled={activePreferences.blocked || !message.trim()} aria-label="发送消息">
                <Send size={22} />
              </button>
            </form>
          </>
        ) : (
          <div className="chat-empty-state">
            <Headphones size={30} />
            <strong>暂无会话</strong>
            <p>发起私信或服务沟通后，会话会显示在这里。</p>
          </div>
        )}
      </div>
    </section>
  );
}
